import React, { useEffect, useMemo, useState } from 'react'
import isNil from 'lodash/isNil'
import { Drawer, Empty, Spin, Tabs } from 'antd'
import { RbacActions } from 'enrollment-platform/rbac/constants'
import { useUsRestrictedDataAccess } from 'enrollment-platform/rbac/useUsRestrictedDataAccess'
import { useGetGeneratedInvoiceDetailQuery } from '../../../api/generated-invoice-api-client'
import { FundingInvoiceListItemDto } from '../../../api/dto/generated-invoice.dto'
import { CensusTable } from './CensusTable'
import { CalculatedFeesSummary } from './CalculatedFeesSummary'
import { InvoiceDataGrid } from './InvoiceDataGrid'
import { InvoiceSummaryTab } from './InvoiceSummaryTab'
import {
  adjustmentColumns,
  healthPlanColumns,
  type InvoiceLineRow,
  medicareColumns,
  ytdColumns,
} from './generatedInvoiceColumns'
import { type InvoiceLineSplit, splitInvoiceLines } from './splitInvoiceLines'
import {
  DrawerHeader,
  DrawerHeaderMeta,
  DrawerHeaderTitle,
  DrawerLoading,
  DrawerTablePane,
  DrawerTabEmptyState,
  DrawerTabsContainer,
  drawerBodyStyle,
  StatusBadge,
} from './InvoiceGenerationView.styles'
import { BILLING_MONTH_ORDER, formatStatusLabel } from './utils'

interface GeneratedInvoiceDrawerProps {
  invoiceId: string | null
  selectedInvoice: FundingInvoiceListItemDto | null
  onClose: () => void
}

// Any numeric value enables AntD's fixed-header + scrollable-body layout; the
// real body height is driven by the flex column in DrawerTablePane.
const TABLE_SCROLL_Y = 400

// Health Plans, Adjustments, Medicare, Census and YTD name employees, so they
// need Read on UsRestrictedData (EMS-10939). EPS returns them empty without it;
// these two tabs read the summary subtotals and fee columns instead.
const TABS_WITHOUT_US_DATA = new Set(['summary', 'fees'])

const EMPTY_SPLIT: InvoiceLineSplit = {
  premiums: [],
  adjustments: [],
  planCosts: 0,
  adjustmentsTotal: 0,
  isDerived: false,
}

const GeneratedInvoiceDrawer: React.FC<GeneratedInvoiceDrawerProps> = ({
  invoiceId,
  selectedInvoice,
  onClose,
}) => {
  const { data, isLoading } = useGetGeneratedInvoiceDetailQuery(invoiceId)
  const summary = data?.summary
  const ytd = data?.ytd ?? []

  // Census and Calculated Fees are read straight from the invoice detail, which
  // returns exactly what was captured at generate time (census snapshot + stored
  // fee columns). This makes both tabs deterministic — they reflect what was
  // charged, not a live re-price. Invoices without a captured snapshot (non-
  // billable or pre-snapshot) come back with empty census / null fees, which
  // render as an empty state rather than falling back to a live call.
  const census = data?.census ?? []
  const medicare = data?.medicare ?? []
  const fees = data?.fees ?? null
  const [activeTab, setActiveTab] = useState('summary')
  const { hasUsRestrictedDataAccess: canViewUsData } =
    useUsRestrictedDataAccess(RbacActions.Read)
  const isTabVisible = (key: string): boolean =>
    canViewUsData || TABS_WITHOUT_US_DATA.has(key)

  useEffect(() => {
    setActiveTab('summary')
  }, [invoiceId])

  useEffect(() => {
    if (!invoiceId) return undefined

    const previousBodyOverflow = document.body.style.overflow
    const previousHtmlOverflow = document.documentElement.style.overflow

    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousBodyOverflow
      document.documentElement.style.overflow = previousHtmlOverflow
    }
  }, [invoiceId])

  // The funded (billed) month comes from the invoice's billing-period start — the
  // same basis the backend uses — so the summary, Health Plans tab, and enrolled
  // headcount stay scoped to one month and are year-boundary safe. A funding
  // invoice bills one month at a time; the YTD tab carries the full history.
  const fundedMonth = useMemo(() => {
    const periodStart = summary?.billingPeriodStart
    if (isNil(periodStart)) return null
    const monthIndex = Number(periodStart.slice(5, 7)) - 1
    return BILLING_MONTH_ORDER[monthIndex] ?? null
  }, [summary?.billingPeriodStart])

  // Health Plans tab = the funded month's premiums; Adjustments tab = credits,
  // other-month lines and funded-month corrections. The API decides the split
  // and reports both subtotals; an older response is split here by the funded
  // month (see splitInvoiceLines). The YTD tab carries the cross-invoice history.
  const { premiums, adjustments, planCosts, adjustmentsTotal } =
    useMemo<InvoiceLineSplit>(
      () => (data ? splitInvoiceLines(data, fundedMonth) : EMPTY_SPLIT),
      [data, fundedMonth]
    )

  // Funded-month charge rows across both tabs: every premium, plus a funded-
  // month correction charge (a top-up still means the member has a plan that
  // month). Credits are left out — a funded-month credit means coverage ended.
  // Drives the census enrollment flags / benefit windows; by construction this
  // matches the Calculated Fees "Enrolled" headcount.
  const fundedMonthHealthPlans = useMemo(
    () => [
      ...premiums,
      ...adjustments.filter(
        (row) =>
          row.type === 'CHARGE' &&
          (isNil(fundedMonth) || row.billingMonth === fundedMonth)
      ),
    ],
    [premiums, adjustments, fundedMonth]
  )

  // Employees enrolled in the funded month = those with a premium plan that month.
  const enrolledEmployeeKeys = useMemo(() => {
    const keys = new Set<string>()
    for (const row of fundedMonthHealthPlans) {
      if (!isNil(row.employeePublicKey)) keys.add(row.employeePublicKey)
    }
    return keys
  }, [fundedMonthHealthPlans])

  // Per-enrolled-employee benefit window (effective/end) sourced from the funded-
  // month Health Plans rows — the same dates that drive the enrolled flag. When an
  // employee has multiple benefits that month, widen to their union span (earliest
  // effective, latest end; a null end means open-ended coverage).
  const enrolledBenefitWindowByEmployee = useMemo(() => {
    const map = new Map<
      string,
      { effective: string | null; end: string | null }
    >()
    for (const row of fundedMonthHealthPlans) {
      if (isNil(row.employeePublicKey)) continue
      const existing = map.get(row.employeePublicKey)
      if (isNil(existing)) {
        map.set(row.employeePublicKey, {
          effective: row.benefitEffectiveDate,
          end: row.benefitEndDate,
        })
        continue
      }
      const effective =
        isNil(existing.effective) || isNil(row.benefitEffectiveDate)
          ? (existing.effective ?? row.benefitEffectiveDate)
          : row.benefitEffectiveDate < existing.effective
            ? row.benefitEffectiveDate
            : existing.effective
      const end =
        isNil(existing.end) || isNil(row.benefitEndDate)
          ? null
          : row.benefitEndDate > existing.end
            ? row.benefitEndDate
            : existing.end
      map.set(row.employeePublicKey, { effective, end })
    }
    return map
  }, [fundedMonthHealthPlans])

  return (
    <Drawer
      title={
        <DrawerHeader>
          <div>
            <DrawerHeaderTitle>
              {summary?.period ?? selectedInvoice?.period ?? 'Invoice detail'}
            </DrawerHeaderTitle>
            <DrawerHeaderMeta>
              Issue Date:{' '}
              {summary?.issueDate ?? selectedInvoice?.issueDate ?? '—'}
            </DrawerHeaderMeta>
            <DrawerHeaderMeta>
              Company: {selectedInvoice?.clientName ?? '—'}
            </DrawerHeaderMeta>
          </div>
          <StatusBadge>{formatStatusLabel(summary?.status)}</StatusBadge>
        </DrawerHeader>
      }
      open={!isNil(invoiceId)}
      onClose={onClose}
      placement="bottom"
      height="100%"
      destroyOnClose
      styles={{ body: drawerBodyStyle }}
    >
      {isLoading ? (
        <DrawerLoading>
          <Spin />
        </DrawerLoading>
      ) : isNil(summary) ? (
        <Empty description="Invoice detail is unavailable" />
      ) : (
        <DrawerTabsContainer>
          <Tabs
            activeKey={isTabVisible(activeTab) ? activeTab : 'summary'}
            onChange={setActiveTab}
            items={[
              {
                key: 'summary',
                label: 'Invoice Summary',
                children: (
                  <InvoiceSummaryTab
                    planCosts={planCosts}
                    adjustments={adjustmentsTotal}
                    totalDue={summary.amount}
                    fees={fees}
                    hasAdjustmentsTab={isTabVisible('adjustments')}
                  />
                ),
              },
              {
                key: 'fees',
                label: 'Fees',
                children: fees ? (
                  <CalculatedFeesSummary fees={fees} />
                ) : (
                  <DrawerTabEmptyState>
                    No calculated fees were captured for this invoice.
                  </DrawerTabEmptyState>
                ),
              },
              {
                key: 'health-plans',
                label: 'Health Plans',
                children:
                  premiums.length > 0 ? (
                    <InvoiceDataGrid<InvoiceLineRow>
                      rows={premiums}
                      columnDefs={healthPlanColumns}
                      label="Health Plans line items"
                    />
                  ) : (
                    <DrawerTabEmptyState>
                      No health plan rows were returned for this invoice.
                    </DrawerTabEmptyState>
                  ),
              },
              {
                key: 'adjustments',
                label: 'Adjustments',
                children:
                  adjustments.length > 0 ? (
                    <InvoiceDataGrid<InvoiceLineRow>
                      rows={adjustments}
                      columnDefs={adjustmentColumns}
                      label="Adjustments line items"
                    />
                  ) : (
                    <DrawerTabEmptyState>
                      No adjustments on this invoice.
                    </DrawerTabEmptyState>
                  ),
              },
              {
                key: 'medicare',
                label: 'Medicare',
                children:
                  medicare.length > 0 ? (
                    <InvoiceDataGrid
                      rows={medicare}
                      columnDefs={medicareColumns}
                      label="Medicare line items"
                    />
                  ) : (
                    <DrawerTabEmptyState>
                      No Medicare rows found for this invoice.
                    </DrawerTabEmptyState>
                  ),
              },
              {
                key: 'census',
                label: 'Census',
                children:
                  census.length > 0 ? (
                    <DrawerTablePane>
                      <CensusTable
                        records={census}
                        enrolledKeys={enrolledEmployeeKeys}
                        enrolledBenefitWindows={enrolledBenefitWindowByEmployee}
                        scrollY={TABLE_SCROLL_Y}
                      />
                    </DrawerTablePane>
                  ) : (
                    <DrawerTabEmptyState>
                      No census was captured for this invoice.
                    </DrawerTabEmptyState>
                  ),
              },
              {
                key: 'ytd',
                label: 'YTD',
                children:
                  ytd.length > 0 ? (
                    <InvoiceDataGrid
                      rows={ytd}
                      columnDefs={ytdColumns}
                      label="YTD line items"
                    />
                  ) : (
                    <DrawerTabEmptyState>
                      No YTD rows were returned for this invoice.
                    </DrawerTabEmptyState>
                  ),
              },
            ].filter((tab) => isTabVisible(tab.key))}
          />
        </DrawerTabsContainer>
      )}
    </Drawer>
  )
}

export { GeneratedInvoiceDrawer }
